/**
 * Popover and PopoverButton tenant tokens. Every token is optional and falls
 * back to the default rendering. PopoverButton takes its padding, label size
 * and closed radius from the Button `lg` tokens.
 *
 * - `--cds-popover-color-bg`: panel fill, on the desktop panel and the
 *   small-screen sheet. Unset, it takes the theme's `--neutral-50` when the
 *   theme defines one and `--neutral-100` otherwise (the default scale has no
 *   50). The open trigger follows it, so an attached tab and its panel stay
 *   one color.
 * - `--cds-popover-button-radius-open`: open trigger radius (8px). An attached
 *   trigger keeps its bottom corners square.
 * - `--cds-popover-button-color-bg-hover`: closed trigger fill on hover, over
 *   the Button ghost hover token (transparent).
 * - `--cds-popover-button-color-bg-open`: open trigger fill, over the panel
 *   fill.
 * - `--cds-popover-motion-duration-open` / `--cds-popover-motion-easing-open`:
 *   panel enter and trigger fill-in (150ms, default easing).
 * - `--cds-popover-motion-duration-close` / `--cds-popover-motion-easing-close`:
 *   panel exit and trigger fill-out (100ms, ease-in).
 */
/**
 * How far below the viewport the panel's bottom collision edge sits. Radix
 * flips a panel above its trigger once the room below runs out; with the edge
 * out of reach the panel always opens below, and Radix still shifts it
 * sideways to stay on screen. Radix adds the same amount to the room it
 * reports below the trigger, so `max-h-popover` subtracts it again: the slack
 * `popoverCollisionPadding()` adds and the one `max-h-popover` subtracts must
 * stay the same value.
 */
export const POPOVER_BOTTOM_COLLISION_SLACK = 100_000;

const PANEL_BG =
  'var(--cds-popover-color-bg, var(--neutral-50, var(--neutral-100, #E6E9EC)))';

const OPEN_DURATION =
  'var(--cds-popover-motion-duration-open, var(--motion-duration-fast, 150ms))';
const OPEN_EASING =
  'var(--cds-popover-motion-easing-open, var(--motion-easing-default, cubic-bezier(0.4, 0, 0.2, 1)))';
const CLOSE_DURATION = 'var(--cds-popover-motion-duration-close, 100ms)';
const CLOSE_EASING =
  'var(--cds-popover-motion-easing-close, cubic-bezier(0.4, 0, 1, 1))';

/** Spread each key into the matching `theme.extend` key of the consuming config. */
export const popoverThemeExtend = {
  backgroundColor: {
    'popover-panel': PANEL_BG,
    'popover-button-hover':
      'var(--cds-popover-button-color-bg-hover, var(--cds-button-color-bg-ghost-hover, var(--cds-color-action-bg-ghost-hover, transparent)))',
    'popover-button-open': `var(--cds-popover-button-color-bg-open, ${PANEL_BG})`,
  },
  borderRadius: {
    'popover-button-open': 'var(--cds-popover-button-radius-open, 8px)',
  },
  keyframes: {
    'popover-fade-in': { from: { opacity: '0' }, to: { opacity: '1' } },
    // Ends hidden, so a force-mounted closed panel is not left on the page
    // invisible but still clickable and announced.
    'popover-fade-out': {
      from: { opacity: '1', visibility: 'visible' },
      to: { opacity: '0', visibility: 'hidden' },
    },
  },
  /**
   * One animation per state, so nothing competes for the `animation`
   * shorthand. Opacity only: the panel never moves, so an attached panel's
   * top edge stays on its trigger, and the trigger fill and backdrop fade on
   * the same tokens.
   */
  animation: {
    'popover-fade-in': `popover-fade-in ${OPEN_DURATION} ${OPEN_EASING}`,
    'popover-fade-out': `popover-fade-out ${CLOSE_DURATION} ${CLOSE_EASING} forwards`,
  },
  /** Order matches the step entry in each `transitionTimingFunction` list below. */
  transitionProperty: {
    'popover-button':
      'color, background-color, text-decoration-color, border-radius',
  },
  transitionDuration: {
    'popover-open': OPEN_DURATION,
    'popover-close': CLOSE_DURATION,
  },
  /**
   * The trigger fill fades with the panel. Its corners step instead: they
   * take the open shape at the start of the enter and keep it until the end
   * of the exit, so an attached tab never shows notches while it fades.
   */
  transitionTimingFunction: {
    'popover-open': `${OPEN_EASING}, ${OPEN_EASING}, ${OPEN_EASING}, steps(1, start)`,
    'popover-close': `${CLOSE_EASING}, ${CLOSE_EASING}, ${CLOSE_EASING}, steps(1, end)`,
  },
};
